import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box, Card, CardContent, Typography, CircularProgress, Grid,
  List, ListItemButton, ListItemText, ListItemIcon, Divider, Chip,
  Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import { Warehouse, MapPin, Package, AlertTriangle, ChevronRight } from "lucide-react";
import BreadCrumb from "../../../../components/navbar/Breadcrumb";
import { getEstadisticasKae, getAlmacenes } from "../../../../services";
import { withRouter } from "../../../../providers/withRouter";

const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <Card
    sx={{
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.2s",
      "&:hover": onClick ? { boxShadow: 6, transform: "translateY(-2px)" } : {},
      borderLeft: `4px solid ${color || "#92004D"}`,
      height: "100%",
    }}
    onClick={onClick}
  >
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2.5, p: 3, py: 4 }}>
      <Box
        sx={{
          backgroundColor: color || "#92004D",
          borderRadius: "14px",
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 56,
          minHeight: 56,
        }}
      >
        <Icon size={32} color="#fff" />
      </Box>
      <Box>
        <Typography variant="h3" sx={{ fontWeight: "bold", lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mt: 0.5 }}>
          {label}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const PanelKae = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [almacenes, setAlmacenes] = useState([]);
  const [filtroAlmacen, setFiltroAlmacen] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchEstadisticas(), fetchAlmacenes()]).finally(() => setLoading(false));
  }, []);

  const fetchEstadisticas = async () => {
    try {
      const data = await getEstadisticasKae();
      setStats(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchAlmacenes = async () => {
    try {
      const data = await getAlmacenes();
      const items = data?.data || data || [];
      setAlmacenes(Array.isArray(items) ? items : []);
    } catch (error) {
      console.log(error);
    }
  };

  const detalleFiltrado = (stats?.detalle_bajo_minimo || []).filter(
    (item) => !filtroAlmacen || item.almacen_id === filtroAlmacen
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <CircularProgress style={{ color: "#92004D" }} />
      </div>
    );
  }

  return (
    <>
      <BreadCrumb rutas={[{ path: "/panel-kae", label: t('panel.title') }]} />
      <div className="listPage">
        <div className="gridCard">
          <div className="gridHead">
            {t('panel.title').toUpperCase()}
          </div>
        </div>
      </div>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={MapPin}
              label={t('panel.totalInstallations')}
              value={stats?.total_instalaciones ?? 0}
              color="#0767A2"
              onClick={() => navigate("/instalaciones")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={Warehouse}
              label={t('panel.totalWarehouses')}
              value={stats?.total_almacenes ?? 0}
              color="#2E7D32"
              onClick={() => navigate("/almacenes")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={Package}
              label={t('panel.totalProducts')}
              value={stats?.total_productos ?? 0}
              color="#92004D"
              onClick={() => navigate("/productos")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={AlertTriangle}
              label={t('panel.belowMinimum')}
              value={stats?.stocks_bajo_minimo ?? 0}
              color={stats?.stocks_bajo_minimo > 0 ? "#EE4534" : "#757575"}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Stock bajo mínimo */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", minHeight: 350 }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {t('panel.belowMinimumDetail')}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>{t('panel.filterByWarehouse')}</InputLabel>
                    <Select
                      value={filtroAlmacen}
                      label={t('panel.filterByWarehouse')}
                      onChange={(e) => setFiltroAlmacen(e.target.value)}
                    >
                      <MenuItem value="">{t('panel.allWarehouses')}</MenuItem>
                      {almacenes.map((alm) => (
                        <MenuItem key={alm.id} value={alm.id}>{alm.nombre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {detalleFiltrado.length > 0 ? (
                  <List disablePadding>
                    {detalleFiltrado.map((item, index) => (
                      <div key={item.id}>
                        {index > 0 && <Divider />}
                        <ListItemButton
                          onClick={() => navigate(`/almacenes/${item.almacen_id}`)}
                          sx={{ px: 1, borderRadius: 1 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <AlertTriangle size={18} color="#EE4534" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.producto?.nombre}
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                                  {item.producto?.sku}
                                </Typography>
                              </Typography>
                            }
                            secondary={item.almacen?.nombre}
                          />
                          <Chip
                            label={`${item.cantidad} / ${item.stock_minimo}`}
                            size="small"
                            sx={{
                              backgroundColor: "#FFF3F0",
                              color: "#EE4534",
                              fontWeight: "bold",
                              mr: 1,
                            }}
                          />
                          <ChevronRight size={16} color="#999" />
                        </ListItemButton>
                      </div>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>
                    {t('panel.noStockAlerts')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Lista de almacenes */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", minHeight: 350 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  {t('panel.warehouseList')}
                </Typography>
                {almacenes.length > 0 ? (
                  <List disablePadding>
                    {almacenes.map((alm, index) => (
                      <div key={alm.id}>
                        {index > 0 && <Divider />}
                        <ListItemButton
                          onClick={() => navigate(`/almacenes/${alm.id}`)}
                          sx={{ px: 1, borderRadius: 1 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Warehouse size={18} color="#2E7D32" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {alm.nombre}
                              </Typography>
                            }
                            secondary={[alm.direccion, alm.municipio, alm.provincia].filter(Boolean).join(", ")}
                          />
                          <ChevronRight size={16} color="#999" />
                        </ListItemButton>
                      </div>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>
                    {t('panel.noWarehouses')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default withRouter(PanelKae);
